import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import cn from 'classnames';

import {
	ButtonType,
	DatepickerRange,
	ExportActionsTypes,
	FileTypeSelectionCommonForm,
	IconButton,
	Icons,
	Modal,
	RadioButton,
	SpinnerOverlay,
	SubmissionButtons,
} from '@components/common';
import Granularities from '@components/Kpi/Parameters/Granularities';
import KpiDatesParameters from '@components/Kpi/Parameters/KpiDatesParameters';
import ParametersKpiSelector from '@components/Kpi/Parameters/ParametersKpiSelector/ParametersKpiSelector';
import { useKpiSelectorsLogic } from '@components/Kpi/Parameters/ParametersKpiSelector/useKpiSelectorsLogic';
import SaveButton from '@components/Kpi/Parameters/SaveButton';
import { TechnologySelectors } from '@components/Kpi/Parameters/TechnologySelectors/TechnologySelectors';
import {
	useTechnologySelectorsLogic,
} from '@components/Kpi/Parameters/TechnologySelectors/useTechnologySelectorsLogic';
import { useDynamicDatesLogic } from '@components/Kpi/Parameters/useDynamicDatesLogic';
import { useParametersInitializationLogic } from '@components/Kpi/Parameters/useParametersInitializationLogic';
import { useParametersLogic } from '@components/Kpi/Parameters/useParametersLogic';
import { usePresetLogic } from '@components/Kpi/Parameters/usePresetLogic';
import { useSitesAndVendorLogic } from '@components/Kpi/Parameters/useResetSitesAndVendor';
import { useSubmitAndExportLogic } from '@components/Kpi/Parameters/useSubmitAndExportLogic';
import Preset from '@components/Kpi/Preset';

import { useQueryKpiGroup } from '@hooks/kpis/useQueryKpiGroup';
import { useQueryOptionsGranularities } from '@hooks/kpis/useQueryOptionsGranularities';
import { useQueryOptionsGranularityAutomatic } from '@hooks/kpis/useQueryOptionsGranularityAutomatic';

import {
	endOfDayInUTC,
	prepareDateForRequest,
	startOfDayInMilliseconds,
	startOfDayInUTC,
} from '@utils/kpi-utils/date';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { ILinkActionInterface } from '@components/routes/AdminPanel/ManageCrowdCells/interfaces';
import { IParametersTypes } from './Parameters.types';

import {
	ETechnologiesValues,
	EVendorsValues,
} from '@enums/common';
import {
	EAggregationTypes,
	EDateSelectionModes,
	EDynamicDateRange,
	EFormFields,
	TAggregationTypes,
} from '@enums/kpi-api';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { exportRadioButtonsData } from '@constants/common';
import { getParametersDefaultValues } from '@constants/KPI/parametersDefaultValues';
import { MODAL_WIDTH_455 } from '@constants/modals';
import { invalidRangeError } from '@components/Kpi/Parameters/constants';

import { changeOptionsByVendor, get5GTechnologyLabel } from './utils';

import './Parameters.scss';

const Parameters = ({
	allSelectedSitesOnMap,
	clearPreset,
	closePresetNameForm,
	createPreset,
	defaultSelectedSitesOnMap,
	deletePreset,
	exportChartsFromParams,
	getCharts,
	initialVendor,
	isApplyTechnologyFromPreset = false,
	isLoadingOptions,
	isLoadingPresetForm,
	isLoadingSetPreset = false,
	isMap,
	isOpenRenamePresetForm,
	isPresetModifiedByUser = false,
	openPresetForm,
	options4GFddLimenet,
	options4GFddNokia,
	options4GTddLimenet,
	options4GTddNokia,
	options5GLimenet,
	options5GNokia,
	options5GOran,
	optionsLimenetKpi,
	optionsNokiaKpi,
	optionsVendorsKpi,
	presetSaveDate,
	presetValue,
	presets,
	renamablePreset,
	runProgress,
	selectedPreset,
	setIsDirty,
	setIsLoadingSetPreset,
	setIsWithInterval,
	setPresetValue,
	setRenamablePreset,
	setStateSelectedPreset,
	setVendor,
	togglePresetNameForm,
	updatePreset,
	updatePresetName,
}: IParametersTypes) => {
	const [isOpenFileTypeSelectionModal, setIsOpenFileTypeSelectionModal] = useState(false);
	// DATES
	const defaultFrom = useMemo(() => startOfDayInMilliseconds(new Date()), []);
	const defaultTo = useMemo(() => startOfDayInMilliseconds(new Date()), []);
	// DEFAULT VALUES
	const defaultValues = useMemo(() => getParametersDefaultValues({
		defaultFrom,
		defaultTo,
		optionsVendorsKpi,
		initialVendor,
	}), [defaultFrom, defaultTo, optionsVendorsKpi, initialVendor]);
	// FORM
	const formMethods = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues,
	});

	const {
		vendor,
		from,
		to,
		isOranVendor,
		isMapMode,
		isOpenParams,
		toggleParams,
		containerClasses,
		containerBody,
		activeSiteMarkers,
		activeCustomPolygons,
		maxYearInterval,
		minDate,
		isAutomaticGranularity,
		granularity,
		initTreeCountersData,
		treeKpiIds,
		treeCountersIds,
		initTreeKpiData,
		kpiValues,
		includeDateIntervalsFrom,
		includeDateIntervalsTo,
		maxSelection,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	} = useParametersLogic({
		isMap,
		setIsWithInterval,
		formMethods,
		defaultFrom,
	});

	const {
		register,
		handleSubmit,
		setValue,
		getValues,
		resetField,
		watch,
		control,
		formState: { isDirty },
	} = formMethods;

	const {
		dynamicDatesOptions,
		dateRangePreview,
		isLoadingDynamicDates,
		isLoadingDateRangePreview,
	} = useDynamicDatesLogic({
		vendor,
		getValues,
	});

	const {
		defaultValueT_4G_FDD,
		defaultValueT_4G_TDD,
		defaultValueT_5G,
		onChangeSitesT_4G_FDD,
		onChangeSitesT_4G_TDD,
		onChangeSitesT_5G,
		resetT_4G_FDD,
		resetT_4G_TDD,
		resetT_5G,
		setDefaultValueT_4G_FDD,
		setDefaultValueT_4G_TDD,
		setDefaultValueT_5G,
	} = useTechnologySelectorsLogic({
		setIsDirty,
		setValue,
		resetField,
	});

	const { kpiGroupData, countersGroupData, isLoadingKpiGroup } = useQueryKpiGroup({
		vendor: vendor?.value,
		isWithMaxSelection: !!maxSelection,
	});

	const {
		defaultValueKpi,
		treeKpi,
		treeCounters,
		optionsKpi,
		selectedKpiNumber,
		selectedCountersNumber,
		maxSelectionText,
		isMaxSelectionLimitErrorMessageShown,
		isMaxSelectionLimitErrorMessageCountersShown,
		onChangeKpi,
		resetKpi,
		handleUpdateTreeKpi,
		handleUpdateTreeCounters,
		setTreeKpi,
		setTreeCounters,
		setTreeKpiIds,
		setTreeCountersIds,
		setSelectedKpiNumber,
		setSelectedCountersNumber,
		setDefaultValueKpi,
		setInitKpiData,
		setInitCountersData,
		//reuse for Parameters and Presets
	} = useKpiSelectorsLogic({
		kpiGroupData,
		countersGroupData,
		setValue,
		resetField,
		setIsDirty,
		vendor,
		optionsLimenetKpi,
		optionsNokiaKpi,
		maxSelection,
	});

	const {
		onSubmit,
		exportHandler,
		prepareDataForSubmit,
		getKpiIds,
	} = useSubmitAndExportLogic({
		getValues,
		handleSubmit,
		vendor,
		treeKpiIds,
		treeCountersIds,
		isMapMode,
		activeSiteMarkers,
		activeCustomPolygons,
		runProgress,
		getCharts,
		presetValue,
		exportChartsFromParams,
	});

	const { resetSitesAndVendor } = useSitesAndVendorLogic({
		setValue,
		optionsVendorsKpi,
		onChangeSitesT_5G,
		onChangeSitesT_4G_FDD,
		onChangeSitesT_4G_TDD,
		resetT_5G,
		resetT_4G_FDD,
		resetT_4G_TDD,
		options4GFddLimenet,
		options4GFddNokia,
		options4GTddLimenet,
		options4GTddNokia,
		options5GLimenet,
		options5GNokia,
		options5GOran,
		vendor,
	});

	const handleUpdateTreeWithWorker = useCallback(
		(tree: IFlatMoTreeNode[], kpiIds: string[]): Promise<IFlatMoTreeNode[]> => {
			const treeUpdaterWorker = new Worker(new URL('./treeUpdaterWorker.js', import.meta.url), {
				type: 'module',
			});

			return new Promise((resolve) => {
				treeUpdaterWorker.postMessage({ data: tree, kpiIds });

				treeUpdaterWorker.onmessage = (e: MessageEvent<IFlatMoTreeNode[]>) => {
					treeUpdaterWorker.terminate();
					resolve(e.data);
				};
			});
		},
		[],
	);

	const {
		setSelectedPreset,
		savePreset,
		editPreset,
	} = usePresetLogic({
		selectedPreset,
		presetValue,
		minDate,
		optionsKpi,
		kpiGroupData,
		countersGroupData,
		treeKpi,
		treeCounters,
		vendor,
		setValue,
		resetField,
		onChangeKpi,
		resetKpi,
		handleUpdateTreeWithWorker,
		setTreeKpi,
		setTreeCounters,
		setTreeKpiIds,
		setTreeCountersIds,
		setInitKpiData,
		setInitCountersData,
		setIsDirty,
		openPresetForm,
		updatePreset,
		getValues,
		isOranVendor,
		defaultSelectedSitesOnMap,
		resetSitesAndVendor,
		defaultFrom,
		defaultTo,
		setIsLoadingSetPreset,
		prepareDataForSubmit,
		getKpiIds,
		dynamicDatesOptions,
	});

	const {
		optionsGranularities,
		isLoadingOptionsGranularities,
	} = useQueryOptionsGranularities({
		isOranVendor,
		from: prepareDateForRequest(from, startOfDayInUTC),
		to: prepareDateForRequest(to, endOfDayInUTC),
		vendor: EVendorsValues.oran,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	});

	const {
		automaticGranularity,
		isLoadingOptionsAutomaticGranularity,
	} = useQueryOptionsGranularityAutomatic({
		isOranVendor: true,
		vendor: EVendorsValues.oran,
		from: prepareDateForRequest(from, startOfDayInUTC),
		to: prepareDateForRequest(to, endOfDayInUTC),
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	});

	const options5G = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			optionsLimenet: options5GLimenet,
			optionsNokia: options5GNokia,
			options5GOran,
			allSelectedSitesOnMap,
			technology: ETechnologiesValues.T_5G,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		vendor?.value,
		options5GLimenet.length,
		options5GNokia.length,
		allSelectedSitesOnMap,
		options5GOran?.length,
	]);

	const options4GFdd = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			optionsLimenet: options4GFddLimenet,
			optionsNokia: options4GFddNokia,
			allSelectedSitesOnMap,
			technology: ETechnologiesValues.T_4G_FDD,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, options4GFddLimenet.length, options4GFddNokia.length, allSelectedSitesOnMap]);

	const options4GTdd = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			optionsLimenet: options4GTddLimenet,
			optionsNokia: options4GTddNokia,
			allSelectedSitesOnMap,
			technology: ETechnologiesValues.T_4G_TDD,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor?.value, options4GTddLimenet.length, options4GTddNokia.length, allSelectedSitesOnMap]);

	useEffect(() => {
		setIsDirty(isDirty);
	}, [isDirty, setIsDirty]);

	useEffect(() => {
		if (!defaultValueT_4G_FDD.length || defaultValueT_4G_TDD.length || !defaultValueT_5G.length) return;
		if (!defaultSelectedSitesOnMap) return;

		resetSitesAndVendor(defaultSelectedSitesOnMap);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultSelectedSitesOnMap]);

	const setFromDate = useCallback((date: number | null) => {
		setValue(EFormFields.from, date);
		setValue(EFormFields.granularity, null);

		setIsDirty(true);
	}, [setIsDirty, setValue]);

	const setToDate = useCallback((date: number | null) => {
		setValue(EFormFields.to, date);
		setValue(EFormFields.granularity, null);

		setIsDirty(true);
	}, [setIsDirty, setValue]);

	const onChangeAggregationType = useCallback((value: TAggregationTypes) => {
		setValue(EFormFields.aggregationType, value);

		setIsDirty(true);
	}, [setIsDirty, setValue]);

	const clearKpiFields = useCallback((isWithVendor = true) => {
		if (!isMap) {
			setDefaultValueT_4G_FDD([]);
			setDefaultValueT_4G_TDD([]);
			setDefaultValueT_5G([]);

			if (isWithVendor) {
				setValue(EFormFields.vendor, optionsVendorsKpi.find(vendor => vendor.isDefault));
			}
		}

		setValue(EFormFields.aggregationType, EAggregationTypes.aggregated);
		setValue(EFormFields.from, defaultFrom);
		setValue(EFormFields.to, defaultTo);
		resetField(EFormFields.timeAggregationType);

		if (isOranVendor) {
			setValue(EFormFields.dateSelectionMode, EDateSelectionModes.static);
			resetField(EFormFields.dynamicDateRange);
			resetField(EFormFields.startDateOffset);
			resetField(EFormFields.granularity);
			setValue(EFormFields.isAutomaticGranularity, true);

			setInitKpiData(kpiGroupData);
			setInitCountersData(countersGroupData);

			setTreeKpi(kpiGroupData);
			setTreeCounters(countersGroupData);

			setTreeKpiIds([]);
			setTreeCountersIds([]);
		} else {
			setDefaultValueKpi([]);
			setValue(EFormFields.kpi, []);
		}

		setIsDirty(true);
		clearPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		kpiGroupData.length,
		countersGroupData.length,
		setTreeKpi,
		setTreeCounters,
		clearPreset,
		isMap,
		isOranVendor,
		optionsVendorsKpi,
		resetField,
		setIsDirty,
		setTreeKpiIds,
		setTreeCountersIds,
		setValue,
	]);

	useParametersInitializationLogic({
		allSelectedSitesOnMap,
		defaultSelectedSitesOnMap,
		initialVendor,
		isApplyTechnologyFromPreset,
		isMap,
		optionsVendorsKpi,
		vendor,
		setValue,
		resetSitesAndVendor,
		setTreeKpi,
		setInitKpiData,
		setTreeCounters,
		setInitCountersData,
		kpiGroupData,
		countersGroupData,
		options5G,
		options4GFdd,
		options4GTdd,
		clearKpiFields,
		selectedPreset,
		setSelectedPreset,
		setVendor,
	});

	const toggleFileTypeSelectionModal = () => setIsOpenFileTypeSelectionModal(prevState => !prevState);

	const closeTypeSelectionForm = () => setIsOpenFileTypeSelectionModal(false);

	const isLoadingGranularities = isLoadingOptionsGranularities || isLoadingOptionsAutomaticGranularity
		|| isLoadingKpiGroup;

	const saveButtonActions: Array<ILinkActionInterface> = useMemo(() => {
		const actions = [
			{
				name: 'save',
				label: 'Save as new preset',
				action: savePreset,
			},
		];

		if (presetValue && selectedPreset) {
			actions.push({
				name: 'change',
				label: 'Save changes to current preset',
				action: editPreset,
			});
		}

		return actions;
	}, [editPreset, presetValue, savePreset, selectedPreset]);

	const isDisableSaveButtonByGranularity = isOranVendor && !(granularity || isAutomaticGranularity);

	const isDisabledSaveButton = isDisableSaveButtonByGranularity
		|| isMaxSelectionLimitErrorMessageShown
		|| isMaxSelectionLimitErrorMessageCountersShown;

	const isStaticDateSelectionMode = dateSelectionMode == EDateSelectionModes.static;
	const isCustomDateRange = dynamicDateRange?.value === EDynamicDateRange.custom;
	const isFilledStartDateOffset = startDateOffset?.value != null;

	const isDateSelectionInvalid= isStaticDateSelectionMode
		? (!from || !to)
		: isCustomDateRange
			? !isFilledStartDateOffset
			: !dynamicDateRange;

	const isDisabledApply = (!defaultValueT_4G_FDD.length && !defaultValueT_4G_TDD.length && !defaultValueT_5G.length)
		|| (!defaultValueKpi.length && !treeKpiIds.length && !treeCountersIds.length)
		|| (isOranVendor && (isAutomaticGranularity ? false : !granularity))
		|| !vendor
		|| isDateSelectionInvalid;

	const isDisabledExportButton = isDisabledApply || isMaxSelectionLimitErrorMessageShown
		|| isMaxSelectionLimitErrorMessageCountersShown;

	return (
		<div className={containerClasses}>
			{(isLoadingOptions || isLoadingSetPreset || isLoadingGranularities) && <SpinnerOverlay/>}
			<Modal
				isOpen={isOpenFileTypeSelectionModal}
				onClose={toggleFileTypeSelectionModal}
				title='File extension to export'
				width={MODAL_WIDTH_455}
			>
				<FileTypeSelectionCommonForm
					activeExportAction={ExportActionsTypes.all}
					closeModals={closeTypeSelectionForm}
					exportRadioButtonsData={exportRadioButtonsData}
					handlePreparingExport={exportHandler}
					isLoading={false}
					toggleModal={toggleFileTypeSelectionModal}
				/>
			</Modal>
			{!isMap &&
				<div className='f5gParametersTitle'>
					{isOpenParams
						&& <div className='f5gParametersTextTitle'>
							Parameters
						</div>
					}
					<div
						className='f5gParametersIcon'
						onClick={toggleParams}
					>
						{isOpenParams ? Icons.chevronsLeft() : Icons.chevronsRight()}
					</div>
				</div>
			}
			<div className={containerBody}>
				<div className={cn({ 'f5gParametersOnMapPresets': isMap })}>
					{isMap && (
						<div className='f5gParametersOnMapPresetsTitle'>
							Presets
						</div>
					)}
					<Preset
						clearHandler={clearKpiFields}
						closePresetNameForm={closePresetNameForm}
						createPreset={createPreset}
						deletePreset={deletePreset}
						isEditable={isOranVendor}
						isLoadingPresetForm={isLoadingPresetForm}
						isMap={isMap}
						isOpenRenamePresetForm={isOpenRenamePresetForm}
						isOranVendor={isOranVendor}
						isPresetModifiedByUser={isPresetModifiedByUser}
						maxDateInterval={maxYearInterval}
						minHeight={isMap ? undefined : '28px'}
						presetSaveDate={presetSaveDate}
						presetValue={presetValue}
						presets={presets}
						propsPresetForm={{
							isLoadingOptions,
							openPresetForm,
							options5GOran,
							optionsVendorsKpi,
							updatePreset,
						}}
						renamablePreset={renamablePreset}
						setPresetValue={setPresetValue}
						setRenamablePreset={setRenamablePreset}
						setStateSelectedPreset={setStateSelectedPreset}
						togglePresetNameForm={togglePresetNameForm}
						updatePresetName={updatePresetName}
					/>
				</div>
				<TechnologySelectors
					defaultValueT_4G_FDD={defaultValueT_4G_FDD}
					defaultValueT_4G_TDD={defaultValueT_4G_TDD}
					defaultValueT_5G={defaultValueT_5G}
					isOranVendor={isOranVendor}
					onChangeSitesT_4G_FDD={onChangeSitesT_4G_FDD}
					onChangeSitesT_4G_TDD={onChangeSitesT_4G_TDD}
					onChangeSitesT_5G={onChangeSitesT_5G}
					options4GFdd={options4GFdd}
					options4GTdd={options4GTdd}
					options5G={options5G}
				/>
				{!isOranVendor
					&& <div className='f5gParametersDate'>
						<DatepickerRange
							errorMessage={invalidRangeError}
							handleChangeFromDate={setFromDate}
							handleChangeToDate={setToDate}
							includeDateIntervalsFrom={includeDateIntervalsFrom}
							includeDateIntervalsTo={includeDateIntervalsTo}
							isClearableFrom
							isClearableTo
							isMobileDevice={false}
							minDateFrom={minDate}
							minDateTo={minDate}
							requiredFromDate
							requiredToDate
							value={{
								from,
								to,
							}}
						/>
					</div>
				}
				{/*Reuse for preset*/}
				<ParametersKpiSelector
					handleUpdateTreeCounters={handleUpdateTreeCounters}
					handleUpdateTreeKpi={handleUpdateTreeKpi}
					initTreeCountersData={initTreeCountersData}
					initTreeKpiData={initTreeKpiData}
					isMaxSelectionLimitErrorMessageCountersShown={isMaxSelectionLimitErrorMessageCountersShown}
					isMaxSelectionLimitErrorMessageShown={isMaxSelectionLimitErrorMessageShown}
					isOranVendor={isOranVendor}
					kpiValues={kpiValues}
					maxSelection={maxSelection}
					maxSelectionText={maxSelectionText}
					onChangeKpi={onChangeKpi}
					optionsKpi={optionsKpi}
					selectedCountersNumber={selectedCountersNumber}
					selectedKpiNumber={selectedKpiNumber}
					setInitCountersData={setInitCountersData}
					setInitKpiData={setInitKpiData}
					setIsDirty={setIsDirty}
					setSelectedCountersNumber={setSelectedCountersNumber}
					setSelectedKpiNumber={setSelectedKpiNumber}
					setTreeCounters={setTreeCounters}
					setTreeCountersIds={setTreeCountersIds}
					setTreeKpi={setTreeKpi}
					setTreeKpiIds={setTreeKpiIds}
					treeCounters={treeCounters}
					treeCountersIds={treeCountersIds}
					treeKpi={treeKpi}
					treeKpiIds={treeKpiIds}
				/>
				<div className='f5gParametersDisplay'>
					<div className='f5gParametersBodyLabel'>
						Display as:
					</div>
					<div className='f5gParametersDisplayButtons'>
						<div className='f5gParametersDisplayButtonsTitle'>
							Aggregated KPI
						</div>
						<RadioButton
							checked={watch(EFormFields.aggregationType) === EAggregationTypes.aggregated}
							id={EAggregationTypes.aggregated}
							label='Aggregated KPI'
							name='aggregationType'
							onChange={onChangeAggregationType}
							register={register}
							value={EAggregationTypes.aggregated}
						/>
					</div>
					<div className='f5gParametersDisplayButtons'>
						<div className='f5gParametersDisplayButtonsTitle'>
							{get5GTechnologyLabel(isOranVendor)} level KPI
						</div>
						<RadioButton
							checked={watch(EFormFields.aggregationType) === EAggregationTypes.siteLevel}
							id={EAggregationTypes.siteLevel}
							label={`Aggregated cells value for a ${get5GTechnologyLabel(isOranVendor)}`}
							name={EFormFields.aggregationType}
							onChange={onChangeAggregationType}
							register={register}
							value={EAggregationTypes.siteLevel}
						/>
						<RadioButton
							checked={watch(EFormFields.aggregationType) === EAggregationTypes.siteLevelAggregatedCells}
							id={EAggregationTypes.siteLevelAggregatedCells}
							label={`Separate lines for each cell of a ${get5GTechnologyLabel(isOranVendor)}`}
							name={EFormFields.aggregationType}
							onChange={onChangeAggregationType}
							register={register}
							value={EAggregationTypes.siteLevelAggregatedCells}
						/>
					</div>
				</div>
				{isOranVendor
					&& <>
						<KpiDatesParameters
							control={control}
							dateRangePreview={dateRangePreview}
							dynamicDatesOptions={dynamicDatesOptions}
							dynamicId='dynamicModeIdParameters'
							from={from}
							includeDateIntervalsFrom={includeDateIntervalsFrom}
							includeDateIntervalsTo={includeDateIntervalsTo}
							isLoadingDateRangePreview={isLoadingDateRangePreview}
							isLoadingDynamicDates={isLoadingDynamicDates}
							minDate={minDate}
							register={register}
							setFromDate={setFromDate}
							setIsDirty={setIsDirty}
							setToDate={setToDate}
							setValue={setValue}
							staticId='staticModeIdParameters'
							to={to}
							watch={watch}
						/>
						<Granularities
							automaticGranularity={automaticGranularity}
							automaticId='automaticIdGranularityParameters'
							control={control}
							granularities={optionsGranularities}
							granularity={granularity}
							isAutomaticGranularity={isAutomaticGranularity}
							manualId='manualIdGranularityParameters'
							setValue={setValue}
						/>
					</>
				}
			</div>
			<div className='f5gParametersSubmissionButtons'>
				<div className='f5gParametersControlButtons'>
					{isOranVendor
						? <SaveButton
							actions={saveButtonActions}
							isDisabled={isDisabledSaveButton}
							savePreset={savePreset}
						/>
						: <IconButton
							icon={Icons.saveIcon}
							isDisabled={isDisabledSaveButton}
							onClick={savePreset}
							tooltipLabel='Save as preset'
						/>
					}
					<IconButton
						color='currentColor'
						icon={Icons.export}
						isDisabled={isDisabledExportButton}
						onClick={toggleFileTypeSelectionModal}
						tooltipLabel={isDisabledExportButton
							? 'To export dataset select all mandatory parameters'
							: 'Export dataset'
						}
					/>
				</div>
				<SubmissionButtons
					disabledPositive={isDisabledApply || isMaxSelectionLimitErrorMessageShown
						|| isMaxSelectionLimitErrorMessageCountersShown}
					negativeHandler={clearKpiFields}
					negativeLabel='Clear all'
					positiveHandler={onSubmit}
					positiveLabel='Apply'
					tooltipPositiveButton={isDisabledApply ? 'Select mandatory parameters' : undefined}
					type={ButtonType.button}
				/>
			</div>
		</div>
	);
};

export default memo(Parameters);
